import { useQuery } from '@tanstack/react-query'
import { BookOpenText, ClipboardList, Pencil, Plus, Trash2, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import { errorMessage } from '@/api/errors'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { PageHeader } from '@/components/ui/PageHeader'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { useAuth } from '@/features/auth/useAuth'
import { courseQuery, useDeleteAssignment, useDeleteMaterial } from '@/features/lms/api'
import { AssignmentDialog } from '@/features/lms/components/AssignmentDialog'
import { MaterialCard } from '@/features/lms/components/MaterialCard'
import { MaterialDialog } from '@/features/lms/components/MaterialDialog'
import { lmsBase } from '@/features/lms/status'
import type { Material, TeacherCourse } from '@/features/lms/types'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatDateTime } from '@/utils/format'

type Editing =
  | { kind: 'material'; item?: Material }
  | { kind: 'assignment'; item?: TeacherCourse['assignments'][number] }
  | null
type Deleting = { kind: 'material' | 'assignment'; id: number; title: string } | null

/** A course for its teachers: contents grouped by unit, and homework with hand-in progress. */
export function LmsCoursePage() {
  const { t } = useTranslation('lms')
  const { portal } = useAuth()
  const params = useParams()
  const sectionId = Number(params.sectionId)
  const subjectId = Number(params.subjectId)
  const course = useQuery(courseQuery(sectionId, subjectId))
  const [tab, setTab] = useState('contents')
  const [editing, setEditing] = useState<Editing>(null)
  const [deleting, setDeleting] = useState<Deleting>(null)
  const deleteMaterial = useDeleteMaterial()
  const deleteAssignment = useDeleteAssignment()
  const base = lmsBase(portal)
  useDocumentTitle(
    course.data
      ? `${course.data.course.subject.name} · ${course.data.course.section.name}`
      : 'Online learning',
  )

  if (course.isPending) return <LoadingState />
  if (course.error)
    return <ErrorState description={errorMessage(course.error)} onRetry={() => void course.refetch()} />
  const c = course.data
  const units = [...new Set(c.materials.map((m) => m.unit ?? ''))]

  const confirmDelete = async () => {
    if (!deleting) return
    try {
      await (deleting.kind === 'material' ? deleteMaterial : deleteAssignment).mutateAsync(deleting.id)
      toast.success(t('Deleted'), deleting.title)
      setDeleting(null)
    } catch (e) {
      toast.error(t('Could not delete'), errorMessage(e))
      setDeleting(null)
    }
  }

  return (
    <>
      <PageHeader
        title={c.course.subject.name}
        description={`${c.course.section.name} · ${t('{{count}} students', { count: c.students })}`}
        breadcrumbs={[
          { label: t('Online learning'), to: base },
          { label: `${c.course.subject.name} · ${c.course.section.name}` },
        ]}
        homeHref={portal === 'staff' ? '/staff' : '/admin'}
        actions={
          tab === 'contents' ? (
            <Button leftIcon={<Plus />} onClick={() => setEditing({ kind: 'material' })}>
              {t('Add content')}
            </Button>
          ) : (
            <Button leftIcon={<Plus />} onClick={() => setEditing({ kind: 'assignment' })}>
              {t('New assignment')}
            </Button>
          )
        }
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label={t('Course')}>
          <TabsTrigger value="contents" icon={<BookOpenText />} count={c.materials.length}>
            {t('Contents')}
          </TabsTrigger>
          <TabsTrigger value="assignments" icon={<ClipboardList />} count={c.assignments.length}>
            {t('Assignments')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contents">
          {c.materials.length === 0 ? (
            <Card>
              <EmptyState
                icon={<BookOpenText />}
                title={t('No content yet')}
                description={t('Publish a first lesson, a worksheet, a link or a video.')}
                action={
                  <Button leftIcon={<Plus />} onClick={() => setEditing({ kind: 'material' })}>
                    {t('Add content')}
                  </Button>
                }
              />
            </Card>
          ) : (
            <div className="space-y-8">
              {units.map((unit) => (
                <section key={unit || 'none'} aria-label={unit || t('Other')}>
                  <h2 className="mb-3 text-sm font-semibold tracking-wide text-muted uppercase">
                    {unit || t('Other')}
                  </h2>
                  <div className="space-y-3">
                    {c.materials
                      .filter((m) => (m.unit ?? '') === unit)
                      .map((m) => (
                        <MaterialCard
                          key={m.id}
                          material={m}
                          actions={
                            <>
                              <IconButton
                                label={t('Edit {{title}}', { title: m.title })}
                                icon={<Pencil />}
                                onClick={() => setEditing({ kind: 'material', item: m })}
                              />
                              <IconButton
                                label={t('Delete {{title}}', { title: m.title })}
                                icon={<Trash2 />}
                                onClick={() => setDeleting({ kind: 'material', id: m.id, title: m.title })}
                              />
                            </>
                          }
                        />
                      ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="assignments">
          {c.assignments.length === 0 ? (
            <Card>
              <EmptyState
                icon={<ClipboardList />}
                title={t('No assignments yet')}
                description={t('Set homework with a due date; students hand it in online.')}
                action={
                  <Button leftIcon={<Plus />} onClick={() => setEditing({ kind: 'assignment' })}>
                    {t('New assignment')}
                  </Button>
                }
              />
            </Card>
          ) : (
            <Card className="divide-y divide-line">
              {c.assignments.map((a) => {
                const past = new Date(a.dueAt) < new Date()
                const pct = a.stats.students ? (a.stats.submitted / a.stats.students) * 100 : 0
                return (
                  <div key={a.id} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={`${base}/assignments/${a.id}`}
                          className="font-medium text-ink hover:text-brand-700 hover:underline"
                        >
                          {a.title}
                        </Link>
                        {!a.published ? (
                          <Badge tone="warning" size="sm">
                            {t('Draft')}
                          </Badge>
                        ) : past ? (
                          <Badge tone="neutral" size="sm">
                            {a.acceptLate ? t('Due date passed · late work accepted') : t('Closed')}
                          </Badge>
                        ) : (
                          <Badge tone="brand" size="sm">
                            {t('Open')}
                          </Badge>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-muted">
                        {t('Due {{date}}', { date: formatDateTime(a.dueAt) })}
                        {a.maxPoints ? ` · ${t('out of {{max}}', { max: a.maxPoints })}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 sm:w-72">
                      <div className="min-w-0 flex-1">
                        <div className="flex justify-between text-xs text-muted">
                          <span className="inline-flex items-center gap-1">
                            <Users className="size-3.5" aria-hidden="true" />
                            {t('{{submitted}} / {{students}} handed in', a.stats)}
                          </span>
                          <span>{t('{{count}} graded', { count: a.stats.graded })}</span>
                        </div>
                        <div className="mt-1.5 h-1.5 rounded-full bg-surface-sunken" aria-hidden="true">
                          <div className="h-full rounded-full bg-brand-500" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                      <IconButton
                        label={t('Edit {{title}}', { title: a.title })}
                        icon={<Pencil />}
                        onClick={() => setEditing({ kind: 'assignment', item: a })}
                      />
                      <IconButton
                        label={t('Delete {{title}}', { title: a.title })}
                        icon={<Trash2 />}
                        onClick={() => setDeleting({ kind: 'assignment', id: a.id, title: a.title })}
                      />
                    </div>
                  </div>
                )
              })}
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {editing?.kind === 'material' ? (
        <MaterialDialog
          sectionId={sectionId}
          subjectId={subjectId}
          material={editing.item}
          units={units.filter(Boolean)}
          onOpenChange={(o) => !o && setEditing(null)}
        />
      ) : null}
      {editing?.kind === 'assignment' ? (
        <AssignmentDialog
          sectionId={sectionId}
          subjectId={subjectId}
          assignment={editing.item}
          onOpenChange={(o) => !o && setEditing(null)}
        />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setDeleting(null)}
          title={t('Delete “{{title}}”?', { title: deleting.title })}
          description={
            deleting.kind === 'material'
              ? t('Its files are deleted too. Students will no longer see it.')
              : t('Only possible while nobody has handed in work.')
          }
          confirmLabel={t('Delete')}
          tone="danger"
          loading={deleteMaterial.isPending || deleteAssignment.isPending}
          onConfirm={() => void confirmDelete()}
        />
      ) : null}
    </>
  )
}
